export type TerritoryCode = 'jm' | 'tt' | 'gy' | 'bs' | 'lc';

export interface TerritoryConfig {
  code: TerritoryCode;
  name: string;
  demonym: string;
  flag: string;
  examName: string;
  examFullName: string;
  grades: string;
  gradeNumbers: number[];
  currency: string;
  currencySymbol: string;
  stripeCurrency: string;
  monthlyPrice: string;
  yearlyPrice: string;
  monthlyPriceCents: number;
  yearlyPriceCents: number;
  yearlySavings: string;
  hero: {
    headline: string;
    highlightedText: string;
    description: string;
    trustLine: string;
  };
  socialProof: {
    tagline: string;
  };
  cta: {
    description: string;
  };
  footer: {
    tagline: string;
  };
  programs: {
    headline: string;
    description: string;
    items: Array<{
      title: string;
      description: string;
      badge?: string;
    }>;
  };
  seo: {
    title: string;
    description: string;
  };
}

/**
 * CSEC and CAPE are set by the Caribbean Examinations Council for the whole region, so unlike the
 * primary exams (PEP in Jamaica, SEA in Trinidad and Tobago, NGSA in Guyana, GLAT in The Bahamas,
 * Common Entrance in St Lucia) they are identical in every territory we sell into. That makes them
 * the cheapest expansion we have: the same content is correct everywhere on day one, with no
 * per-country syllabus work.
 */
export const REGIONAL_EXAMS = [
  {
    code: 'CSEC',
    name: 'CSEC',
    fullName: 'Caribbean Secondary Education Certificate',
    stage: 'Grades 10 and 11',
    ages: '14 to 17',
    description: 'The exam that decides sixth form, college and the first job.',
    grading: 'Grades I to VI, with I to III accepted as a pass',
    papers: 'Paper 1 multiple choice, Paper 2 structured and essay, plus the SBA',
  },
  {
    code: 'CAPE',
    name: 'CAPE',
    fullName: 'Caribbean Advanced Proficiency Examination',
    stage: 'Lower and Upper Sixth',
    ages: '16 to 18',
    description: 'Sixth form. Each Unit is three modules and is certified on its own.',
    grading: 'Grades I to VII, with I to V acceptable for further study',
    papers: 'Paper 1 multiple choice, Paper 2 extended response, plus the SBA',
  },
] as const;

export type ExamStage = 'PRIMARY' | 'LOWER_SECONDARY' | 'CSEC' | 'CAPE';

/**
 * What a grade is actually working towards.
 *
 * The platform is modelled on one grade axis but sells three different exams, and treating a
 * grade as interchangeable is how a Grade 11 student ends up being shown PEP Performance Tasks:
 * the dashboard clamped any grade outside 4 to 6 back to 6 rather than asking what the grade was
 * for. Anything exam-specific should gate on this, not on a grade range.
 *
 * PRIMARY is territory-specific (PEP in Jamaica, SEA in Trinidad and Tobago, and so on); CSEC and
 * CAPE are regional and identical everywhere.
 */
export function examStageForGrade(grade: number | null | undefined): ExamStage | null {
  const g = Number(grade);
  if (!Number.isFinite(g)) return null;
  if (g >= 4 && g <= 6) return 'PRIMARY';
  if (g >= 7 && g <= 9) return 'LOWER_SECONDARY';
  if (g === 10 || g === 11) return 'CSEC';
  if (g === 12 || g === 13) return 'CAPE';
  return null;
}

/** The label to show a student for their grade, e.g. "PEP" in Jamaica or "CSEC" anywhere. */
export function examLabelForGrade(grade: number | null | undefined, primaryExamName: string): string | null {
  switch (examStageForGrade(grade)) {
    case 'PRIMARY': return primaryExamName;
    case 'CSEC': return 'CSEC';
    case 'CAPE': return 'CAPE';
    default: return null;
  }
}

/** Grades the platform has content for, grouped for the admin grade switcher. */
export const GRADE_GROUPS: Array<{ stage: ExamStage; label: string; grades: Array<{ grade: number; label: string }> }> = [
  { stage: 'PRIMARY', label: 'Primary exam', grades: [
    { grade: 4, label: 'Grade 4' }, { grade: 5, label: 'Grade 5' }, { grade: 6, label: 'Grade 6' } ] },
  { stage: 'LOWER_SECONDARY', label: 'Lower secondary', grades: [
    { grade: 7, label: 'Grade 7' }, { grade: 8, label: 'Grade 8' }, { grade: 9, label: 'Grade 9' } ] },
  { stage: 'CSEC', label: 'CSEC', grades: [
    { grade: 10, label: 'Grade 10' }, { grade: 11, label: 'Grade 11' } ] },
  { stage: 'CAPE', label: 'CAPE', grades: [
    { grade: 12, label: 'Lower Sixth (Unit 1)' }, { grade: 13, label: 'Upper Sixth (Unit 2)' } ] },
];

export const TERRITORIES: Record<TerritoryCode, TerritoryConfig> = {
  jm: {
    code: 'jm',
    name: 'Jamaica',
    demonym: 'Jamaican',
    flag: '\u{1F1EF}\u{1F1F2}',
    examName: 'PEP',
    examFullName: 'Primary Exit Profile',
    grades: 'Grades 4, 5 & 6',
    gradeNumbers: [4, 5, 6],
    currency: 'JMD',
    currencySymbol: 'J$',
    stripeCurrency: 'jmd',
    monthlyPrice: '$2,500',
    yearlyPrice: '$10,000',
    monthlyPriceCents: 250000,
    yearlyPriceCents: 1000000,
    yearlySavings: 'Save $20,000 JMD per year!',
    hero: {
      headline: "Jamaica's #1 PEP Exam Prep Platform for",
      highlightedText: 'Grades 4, 5 & 6',
      description: "Stop boring study sessions. Start having fun while you learn. Our game-like approach makes PEP prep addictive in the best way.",
      trustLine: '106,067 Jamaican learners taught since 2011',
    },
    socialProof: {
      tagline: 'Trusted Across Jamaica',
    },
    cta: {
      description: 'Join thousands of Jamaican families preparing their children for PEP success. Start today and see the difference in just one week.',
    },
    footer: {
      tagline: "Jamaica's leading PEP exam preparation platform for Grades 4, 5 & 6.",
    },
    programs: {
      headline: 'PEP Preparation Programmes',
      description: 'Specialized programs designed for Jamaican students in grades 4, 5, and 6 preparing for the Primary Exit Profile (PEP) examinations.',
      items: [
        {
          title: 'PEP Math Mastery',
          description: 'Complete mathematics preparation covering Number Theory, Geometry, Measurement, Algebra, and Statistics aligned to the PEP curriculum.',
        },
        {
          title: 'PEP Complete Prep',
          description: 'Full exam preparation across all PEP subjects including Mathematics, Language Arts, Science, and Social Studies.',
          badge: 'Most Popular',
        },
        {
          title: 'PEP English Excellence',
          description: 'Focused Language Arts preparation covering Reading Comprehension, Grammar, Vocabulary, and Writing for PEP success.',
        },
      ],
    },
    seo: {
      title: 'EduFocal Academy - Jamaica\'s #1 PEP Exam Prep Platform',
      description: 'Jamaica\'s PEP, CSEC and CAPE preparation platform. Named the number one edtech company in the Caribbean by TIME magazine, endorsed by the Ministry of Education, teaching Jamaican students since 2011.',
    },
  },

  tt: {
    code: 'tt',
    name: 'Trinidad & Tobago',
    demonym: 'Trinidadian',
    flag: '\u{1F1F9}\u{1F1F9}',
    examName: 'SEA',
    examFullName: 'Secondary Entrance Assessment',
    grades: 'Standard 5',
    gradeNumbers: [5],
    currency: 'TTD',
    currencySymbol: 'TT$',
    stripeCurrency: 'ttd',
    monthlyPrice: '$99',
    yearlyPrice: '$399',
    monthlyPriceCents: 9900,
    yearlyPriceCents: 39900,
    yearlySavings: 'Save TT$789 per year!',
    hero: {
      headline: "Trinidad & Tobago's #1 SEA Exam Prep Platform for",
      highlightedText: 'Standard 5',
      description: "Stop boring study sessions. Start having fun while you learn. Our game-like approach makes SEA prep addictive in the best way.",
      trustLine: 'Number one edtech company in the Caribbean, TIME magazine',
    },
    socialProof: {
      tagline: 'Trusted Across Trinidad & Tobago',
    },
    cta: {
      description: 'Join families across Trinidad & Tobago preparing their children for SEA success. Start today and see the difference in just one week.',
    },
    footer: {
      tagline: "Trinidad & Tobago's leading SEA exam preparation platform for Standard 5.",
    },
    programs: {
      headline: 'SEA Preparation Programmes',
      description: 'Specialized programs designed for Standard 5 students preparing for the Secondary Entrance Assessment (SEA) examination.',
      items: [
        {
          title: 'SEA Math Mastery',
          description: 'Complete mathematics preparation covering Number Operations, Geometry, Measurement, Statistics, and Algebra aligned to the SEA curriculum.',
        },
        {
          title: 'SEA Complete Prep',
          description: 'Full exam preparation across all SEA subjects including Mathematics, English Language Arts, Creative Writing, and Essay Writing.',
          badge: 'Most Popular',
        },
        {
          title: 'SEA English Excellence',
          description: 'Focused English Language Arts preparation covering Comprehension, Grammar, Vocabulary, Creative Writing, and Essay skills for SEA success.',
        },
      ],
    },
    seo: {
      title: 'EduFocal Academy - Trinidad & Tobago\'s #1 SEA Exam Prep Platform',
      description: 'Trinidad & Tobago\'s leading SEA exam preparation platform for Standard 5. Fun, game-based learning for Secondary Entrance Assessment success.',
    },
  },

  gy: {
    code: 'gy',
    name: 'Guyana',
    demonym: 'Guyanese',
    flag: '\u{1F1EC}\u{1F1FE}',
    examName: 'NGSA',
    examFullName: 'National Grade Six Assessment',
    grades: 'Grade 6',
    gradeNumbers: [6],
    currency: 'GYD',
    currencySymbol: 'G$',
    stripeCurrency: 'gyd',
    monthlyPrice: '$3,500',
    yearlyPrice: '$12,000',
    monthlyPriceCents: 350000,
    yearlyPriceCents: 1200000,
    yearlySavings: 'Save GY$30,000 per year!',
    hero: {
      headline: "Guyana's #1 NGSA Exam Prep Platform for",
      highlightedText: 'Grade 6',
      description: "Stop boring study sessions. Start having fun while you learn. Our game-like approach makes NGSA prep addictive in the best way.",
      trustLine: 'Number one edtech company in the Caribbean, TIME magazine',
    },
    socialProof: {
      tagline: 'Trusted Across Guyana',
    },
    cta: {
      description: 'Join families across Guyana preparing their children for NGSA success. Start today and see the difference in just one week.',
    },
    footer: {
      tagline: "Guyana's leading NGSA exam preparation platform for Grade 6.",
    },
    programs: {
      headline: 'NGSA Preparation Programmes',
      description: 'Specialized programs designed for Grade 6 students preparing for the National Grade Six Assessment (NGSA) examination.',
      items: [
        {
          title: 'NGSA Math Mastery',
          description: 'Complete mathematics preparation covering Number Sense, Geometry, Measurement, Statistics, and Problem Solving aligned to the NGSA curriculum.',
        },
        {
          title: 'NGSA Complete Prep',
          description: 'Full exam preparation across all NGSA subjects including Mathematics, English, Science, and Social Studies.',
          badge: 'Most Popular',
        },
        {
          title: 'NGSA English Excellence',
          description: 'Focused English preparation covering Reading Comprehension, Grammar, Vocabulary, and Composition for NGSA success.',
        },
      ],
    },
    seo: {
      title: 'EduFocal Academy - Guyana\'s #1 NGSA Exam Prep Platform',
      description: 'Guyana\'s leading NGSA exam preparation platform for Grade 6. Fun, game-based learning for National Grade Six Assessment success.',
    },
  },

  bs: {
    code: 'bs',
    name: 'The Bahamas',
    demonym: 'Bahamian',
    flag: '\u{1F1E7}\u{1F1F8}',
    examName: 'GLAT',
    examFullName: 'Grade Level Assessment Test',
    grades: 'Grades 3 & 6',
    gradeNumbers: [3, 6],
    currency: 'BSD',
    currencySymbol: 'B$',
    stripeCurrency: 'bsd',
    monthlyPrice: '$15',
    yearlyPrice: '$60',
    monthlyPriceCents: 1500,
    yearlyPriceCents: 6000,
    yearlySavings: 'Save $120 per year!',
    hero: {
      headline: "The Bahamas' #1 GLAT Exam Prep Platform for",
      highlightedText: 'Grades 3 & 6',
      description: "Stop boring study sessions. Start having fun while you learn. Our game-like approach makes GLAT prep addictive in the best way.",
      trustLine: 'Number one edtech company in the Caribbean, TIME magazine',
    },
    socialProof: {
      tagline: 'Trusted Across The Bahamas',
    },
    cta: {
      description: 'Join families across The Bahamas preparing their children for GLAT success. Start today and see the difference in just one week.',
    },
    footer: {
      tagline: "The Bahamas' leading GLAT exam preparation platform for Grades 3 & 6.",
    },
    programs: {
      headline: 'GLAT Preparation Programmes',
      description: 'Specialized programs designed for students in Grades 3 and 6 preparing for the Grade Level Assessment Test (GLAT) examination.',
      items: [
        {
          title: 'GLAT Math Mastery',
          description: 'Complete mathematics preparation covering Number Concepts, Geometry, Measurement, Data Handling, and Problem Solving aligned to the GLAT curriculum.',
        },
        {
          title: 'GLAT Complete Prep',
          description: 'Full exam preparation across all GLAT subjects including Mathematics, Language Arts, Science, and Social Studies.',
          badge: 'Most Popular',
        },
        {
          title: 'GLAT English Excellence',
          description: 'Focused Language Arts preparation covering Reading Comprehension, Grammar, Vocabulary, and Writing for GLAT success.',
        },
      ],
    },
    seo: {
      title: 'EduFocal Academy - The Bahamas\' #1 GLAT Exam Prep Platform',
      description: 'The Bahamas\' leading GLAT exam preparation platform for Grades 3 & 6. Fun, game-based learning for Grade Level Assessment Test success.',
    },
  },

  lc: {
    code: 'lc',
    name: 'St. Lucia',
    demonym: 'St. Lucian',
    flag: '\u{1F1F1}\u{1F1E8}',
    examName: 'Common Entrance',
    examFullName: 'Common Entrance Examination',
    grades: 'Grade 6',
    gradeNumbers: [6],
    currency: 'XCD',
    currencySymbol: 'EC$',
    stripeCurrency: 'xcd',
    monthlyPrice: '$40',
    yearlyPrice: '$150',
    monthlyPriceCents: 4000,
    yearlyPriceCents: 15000,
    yearlySavings: 'Save EC$330 per year!',
    hero: {
      headline: "St. Lucia's #1 Common Entrance Exam Prep Platform for",
      highlightedText: 'Grade 6',
      description: "Stop boring study sessions. Start having fun while you learn. Our game-like approach makes Common Entrance prep addictive in the best way.",
      trustLine: 'Number one edtech company in the Caribbean, TIME magazine',
    },
    socialProof: {
      tagline: 'Trusted Across St. Lucia',
    },
    cta: {
      description: 'Join families across St. Lucia preparing their children for Common Entrance success. Start today and see the difference in just one week.',
    },
    footer: {
      tagline: "St. Lucia's leading Common Entrance exam preparation platform for Grade 6.",
    },
    programs: {
      headline: 'Common Entrance Preparation Programmes',
      description: 'Specialized programs designed for Grade 6 students preparing for the Common Entrance Examination.',
      items: [
        {
          title: 'Common Entrance Math Mastery',
          description: 'Complete mathematics preparation covering Number Operations, Geometry, Measurement, Statistics, and Problem Solving aligned to the Common Entrance curriculum.',
        },
        {
          title: 'Common Entrance Complete Prep',
          description: 'Full exam preparation across all Common Entrance subjects including Mathematics, English Language, Science, and Social Studies.',
          badge: 'Most Popular',
        },
        {
          title: 'Common Entrance English Excellence',
          description: 'Focused English Language preparation covering Reading Comprehension, Grammar, Vocabulary, and Composition for Common Entrance success.',
        },
      ],
    },
    seo: {
      title: 'EduFocal Academy - St. Lucia\'s #1 Common Entrance Exam Prep Platform',
      description: 'St. Lucia\'s leading Common Entrance exam preparation platform for Grade 6. Fun, game-based learning for exam success.',
    },
  },
};

export const DEFAULT_TERRITORY: TerritoryCode = 'jm';

// hasOwnProperty, not a truthy index: TERRITORIES['constructor'] is truthy via
// the prototype chain, so `?country=constructor` would pass a bare lookup.
export function isTerritoryCode(code: string): code is TerritoryCode {
  return Object.prototype.hasOwnProperty.call(TERRITORIES, code.toLowerCase());
}

export function getTerritoryByCode(code: string): TerritoryConfig {
  const normalized = code.toLowerCase();
  return isTerritoryCode(normalized) ? TERRITORIES[normalized] : TERRITORIES[DEFAULT_TERRITORY];
}
